import { useMemo, useCallback } from 'react'

import { isPending } from 'redux/toolkit/api'
import { patchAccountLightweightSettings, PatchOperation } from 'redux/features/settings/settingsApiThunks'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings, BrblExemptEntry } from 'types/Settings'
import routesConfig from 'lib/routesConfig'

export interface State {
  isLoading: boolean
  tableData: BrblExemptEntry[]
}

export interface EventHandlers {
  onAddItem: (item: BrblExemptEntry) => void
  onRemoveItem: (id: string) => void
  onBulkEdit: () => void
}

export type UseBrblExemptLogic = [State, EventHandlers]

export const useBrblExemptLogic = (): UseBrblExemptLogic => {
  const dispatch = useAppDispatch()

  const { accountSettings, updateAccountSettingPending } = useAppSelector(_stores => ({
    accountSettings: _stores.settings.accountSettings,
    updateAccountSettingPending: isPending(_stores.settings.updateAccountSettingsApiStatus)
  }))

  const tableData = useMemo(() => {
    const exemptions = accountSettings.brbl_exempt
    if (!exemptions) return []
    if (Array.isArray(exemptions)) return exemptions as BrblExemptEntry[]
    if (typeof exemptions === 'string') {
      try {
        return JSON.parse(exemptions) as BrblExemptEntry[]
      } catch {
        return []
      }
    }
    return []
  }, [accountSettings])

  const onAddItem = useCallback(
    (item: BrblExemptEntry) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.ADD,
          value: {
            address: item.address,
            netmask: item.netmask,
            comment: item.comment,
            id: item.id
          },
          variable: AvailableSettings.BRBL_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onRemoveItem = useCallback(
    (id: string) => {
      dispatch(
        patchAccountLightweightSettings({
          operation: PatchOperation.REMOVE,
          value: { id },
          variable: AvailableSettings.BRBL_EXEMPT
        })
      )
    },
    [dispatch]
  )

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_BRBL_EXEMPT_BULK_EDIT.goto()
  }, [])

  return useMemo(
    () => [
      {
        isLoading: updateAccountSettingPending,
        tableData
      },
      {
        onAddItem,
        onRemoveItem,
        onBulkEdit
      }
    ],
    [updateAccountSettingPending, tableData, onAddItem, onRemoveItem, onBulkEdit]
  )
}
